import React from 'react';
import { useParams } from 'react-router-dom';

const CONTACT_EMAIL = 'contact@alpes-solutions.com';
const COMPANY = 'Alpes Solutions SpA';
const COMPANY_RUT = '78.031.083-9';
const COMPANY_ADDRESS = 'Antonio Bellet 193, Oficina 302, Santiago, Providencia, Chile';

const content = {
  en: {
    title: 'Privacy Policy',
    updated: 'Fidelize — Last updated May 2025',
    sections: [
      {
        heading: 'Who we are',
        body: COMPANY + '\nRUT: ' + COMPANY_RUT + '\nAddress: ' + COMPANY_ADDRESS + '\nEmail: ' + CONTACT_EMAIL,
      },
      {
        heading: 'What data we collect',
        body: 'When you use Fidelize, we collect only what is necessary to operate the app:\n• Email address or social login identifier (Google / Apple)\n• Display name (optional, set by you)\n• Date of birth (optional, set by you)\n• Gender (optional, set by you)\n• Loyalty card history: points accumulated and rewards redeemed at partner businesses\n• Firebase Cloud Messaging token to deliver push notifications\n\nNo sensitive financial data is ever collected or stored.',
      },
      {
        heading: 'Why we collect it',
        body: 'We use your data exclusively to:\n• Identify your account and keep it secure\n• Record your loyalty points at partner businesses\n• Send you push notifications about rewards and offers (only from businesses you have a card with)\n• Display your accumulated points and gift history in the app',
      },
      {
        heading: 'Third-party services',
        body: 'The app uses Firebase (Google) for:\n• Authentication (Firebase Auth)\n• Push notifications (Firebase Cloud Messaging)\n• Crash reporting (Firebase Crashlytics) — collects device model, OS version, app version, and crash stack traces to help us fix bugs\n\nNo advertising SDKs are used. The app contains no ads.',
      },
      {
        heading: 'How long we keep your data',
        body: 'Your data is retained for as long as your account is active.\n\nIf your account has not been accessed for more than 3 years, it may be automatically purged.\n\nWe do not keep any data after account deletion.',
      },
      {
        heading: 'How to delete your data',
        body: 'You can request full deletion of your account and all associated data at any time:\n\n1. In the app: go to Account → Delete my account.\n2. By email: send a deletion request to ' + CONTACT_EMAIL + '.\n\nAll personal data will be permanently and irreversibly deleted within 30 days of your request.',
      },
      {
        heading: 'Data sharing',
        body: 'We do not sell, rent, or share your personal data with third parties for commercial purposes.\n\nPartner businesses can see the number of points and rewards associated with their own card — they cannot see your data from other businesses.',
      },
      {
        heading: 'Security',
        body: 'All data is transmitted over encrypted connections (HTTPS). Access to our servers is restricted and monitored. No system is 100% secure, but we take data protection seriously.',
      },
      {
        heading: 'Your rights',
        body: 'You may at any time:\n• Correct your display name, gender, or date of birth directly in the app\n• Request a copy of the data we hold about you\n• Request full deletion of your account and all associated data\n\nTo exercise these rights, contact us at ' + CONTACT_EMAIL + '.',
      },
      {
        heading: 'Changes to this policy',
        body: 'We may update this privacy policy from time to time. When we do, you will be prompted to review and accept the updated terms the next time you open the app.',
      },
    ],
  },
  es: {
    title: 'Política de Privacidad',
    updated: 'Fidelize — Última actualización: mayo 2025',
    sections: [
      {
        heading: 'Quiénes somos',
        body: COMPANY + '\nRUT: ' + COMPANY_RUT + '\nDirección: ' + COMPANY_ADDRESS + '\nCorreo: ' + CONTACT_EMAIL,
      },
      {
        heading: 'Qué datos recopilamos',
        body: 'Al usar Fidelize, recopilamos únicamente lo necesario para operar la app:\n• Correo electrónico o identificador de inicio de sesión social (Google / Apple)\n• Nombre de usuario (opcional, definido por ti)\n• Fecha de nacimiento (opcional, definida por ti)\n• Sexo (opcional, definido por ti)\n• Historial de fidelización: puntos acumulados y recompensas canjeadas en comercios adheridos\n• Token Firebase Cloud Messaging para el envío de notificaciones push\n\nNo se recopila ni almacena ningún dato financiero sensible.',
      },
      {
        heading: 'Por qué lo recopilamos',
        body: 'Usamos tus datos exclusivamente para:\n• Identificar tu cuenta y mantenerla segura\n• Registrar tus puntos de fidelidad en los comercios adheridos\n• Enviarte notificaciones push sobre recompensas y ofertas (únicamente de los comercios donde tengas tarjeta)\n• Mostrar tus puntos acumulados e historial de regalos en la app',
      },
      {
        heading: 'Servicios de terceros',
        body: 'La app usa Firebase (Google) para:\n• Autenticación (Firebase Auth)\n• Notificaciones push (Firebase Cloud Messaging)\n• Informes de errores (Firebase Crashlytics) — recopila modelo de dispositivo, versión de OS, versión de la app y trazas de error para ayudarnos a corregir bugs\n\nNo se usan SDKs publicitarios. La app no contiene publicidad.',
      },
      {
        heading: 'Cuánto tiempo conservamos tus datos',
        body: 'Tus datos se conservan mientras tu cuenta esté activa.\n\nSi tu cuenta no se usa durante más de 3 años, puede ser eliminada automáticamente.\n\nNo conservamos ningún dato tras la eliminación de la cuenta.',
      },
      {
        heading: 'Cómo eliminar tus datos',
        body: 'Puedes solicitar la eliminación completa de tu cuenta y todos los datos asociados en cualquier momento:\n\n1. En la app: ve a Cuenta → Eliminar mi cuenta.\n2. Por correo: envía una solicitud de eliminación a ' + CONTACT_EMAIL + '.\n\nTodos los datos personales serán eliminados de forma permanente e irreversible en un plazo de 30 días.',
      },
      {
        heading: 'Compartir datos',
        body: 'No vendemos, alquilamos ni compartimos tus datos personales con terceros con fines comerciales.\n\nLos comercios adheridos pueden ver el número de puntos y recompensas asociados a su propia tarjeta — no pueden ver tus datos de otros comercios.',
      },
      {
        heading: 'Seguridad',
        body: 'Todos los datos se transmiten mediante conexiones cifradas (HTTPS). El acceso a nuestros servidores está restringido y monitoreado. Ningún sistema es 100% seguro, pero nos tomamos la protección de datos muy en serio.',
      },
      {
        heading: 'Tus derechos',
        body: 'Puedes en cualquier momento:\n• Corregir tu nombre, género o fecha de nacimiento directamente en la app\n• Solicitar una copia de los datos que tenemos sobre ti\n• Solicitar la eliminación completa de tu cuenta y todos los datos asociados\n\nPara ejercer estos derechos, contáctanos en ' + CONTACT_EMAIL + '.',
      },
      {
        heading: 'Cambios en esta política',
        body: 'Podemos actualizar esta política de privacidad de vez en cuando. Cuando lo hagamos, se te pedirá que revises y aceptes los términos actualizados la próxima vez que abras la app.',
      },
    ],
  },
  fr: {
    title: 'Politique de confidentialité',
    updated: 'Fidelize — Dernière mise à jour : mai 2025',
    sections: [
      {
        heading: 'Qui sommes-nous',
        body: COMPANY + '\nRUT : ' + COMPANY_RUT + '\nAdresse : ' + COMPANY_ADDRESS + '\nEmail : ' + CONTACT_EMAIL,
      },
      {
        heading: 'Données collectées',
        body: "Lors de l'utilisation de Fidelize, nous collectons uniquement ce qui est nécessaire au fonctionnement de l'application :\n• Adresse email ou identifiant de connexion sociale (Google / Apple)\n• Nom d'utilisateur (facultatif, renseigné par vous)\n• Date de naissance (facultatif, renseignée par vous)\n• Sexe (facultatif, renseigné par vous)\n• Historique de fidélisation : points accumulés et récompenses échangées chez les commerçants partenaires\n• Token Firebase Cloud Messaging pour les notifications push\n\nAucune donnée financière sensible n'est collectée ou stockée.",
      },
      {
        heading: 'Pourquoi nous les collectons',
        body: "Nous utilisons vos données exclusivement pour :\n• Identifier votre compte et le sécuriser\n• Enregistrer vos points de fidélité chez les commerçants partenaires\n• Vous envoyer des notifications push sur les récompenses et offres (uniquement des commerces où vous avez une carte)\n• Afficher vos points accumulés et l'historique de vos cadeaux dans l'application",
      },
      {
        heading: 'Services tiers',
        body: "L'application utilise Firebase (Google) pour :\n• L'authentification (Firebase Auth)\n• Les notifications push (Firebase Cloud Messaging)\n• Les rapports de plantage (Firebase Crashlytics) — collecte le modèle d'appareil, la version d'OS, la version de l'app et les traces d'erreur pour nous aider à corriger les bugs\n\nAucun SDK publicitaire n'est utilisé. L'application ne contient aucune publicité.",
      },
      {
        heading: 'Durée de conservation',
        body: "Vos données sont conservées tant que votre compte est actif.\n\nSi votre compte n'est pas utilisé pendant plus de 3 ans, il peut être automatiquement supprimé.\n\nAucune donnée n'est conservée après la suppression du compte.",
      },
      {
        heading: 'Suppression de vos données',
        body: "Vous pouvez demander la suppression complète de votre compte et de toutes les données associées à tout moment :\n\n1. Dans l'application : Compte → Supprimer mon compte.\n2. Par email : envoyez une demande de suppression à " + CONTACT_EMAIL + ".\n\nToutes les données personnelles seront définitivement et irréversiblement supprimées dans un délai de 30 jours.",
      },
      {
        heading: 'Partage des données',
        body: "Nous ne vendons, ne louons ni ne partageons vos données personnelles avec des tiers à des fins commerciales.\n\nLes commerçants partenaires peuvent voir le nombre de points et récompenses liés à leur propre carte — ils ne peuvent pas voir vos données chez d'autres commerçants.",
      },
      {
        heading: 'Sécurité',
        body: "Toutes les données sont transmises via des connexions chiffrées (HTTPS). L'accès à nos serveurs est restreint et surveillé. Aucun système n'est sécurisé à 100 %, mais nous prenons la protection des données très au sérieux.",
      },
      {
        heading: 'Vos droits',
        body: "Vous pouvez à tout moment :\n• Corriger votre nom, genre ou date de naissance directement dans l'application\n• Demander une copie des données que nous détenons sur vous\n• Demander la suppression complète de votre compte et de toutes les données associées\n\nPour exercer ces droits, contactez-nous à " + CONTACT_EMAIL + '.',
      },
      {
        heading: 'Modifications de cette politique',
        body: "Nous pouvons mettre à jour cette politique de confidentialité. Le cas échéant, vous serez invité à lire et accepter les nouvelles conditions lors de la prochaine ouverture de l'application.",
      },
    ],
  },
  it: {
    title: 'Informativa sulla Privacy',
    updated: 'Fidelize — Ultimo aggiornamento: maggio 2025',
    sections: [
      {
        heading: 'Chi siamo',
        body: COMPANY + '\nRUT: ' + COMPANY_RUT + '\nIndirizzo: ' + COMPANY_ADDRESS + '\nEmail: ' + CONTACT_EMAIL,
      },
      {
        heading: 'Dati che raccogliamo',
        body: "Quando utilizzi Fidelize, raccogliamo solo ciò che è necessario per far funzionare l'app:\n• Indirizzo email o identificatore di accesso social (Google / Apple)\n• Nome visualizzato (facoltativo, impostato da te)\n• Data di nascita (facoltativa, impostata da te)\n• Genere (facoltativo, impostato da te)\n• Storico fedeltà: punti accumulati e premi riscattati presso i commercianti partner\n• Token Firebase Cloud Messaging per l'invio di notifiche push\n\nNessun dato finanziario sensibile viene mai raccolto o memorizzato.",
      },
      {
        heading: 'Perché li raccogliamo',
        body: "Utilizziamo i tuoi dati esclusivamente per:\n• Identificare il tuo account e mantenerlo sicuro\n• Registrare i tuoi punti fedeltà presso i commercianti partner\n• Inviarti notifiche push su premi e offerte (solo dai commercianti dove hai una carta)\n• Mostrare i tuoi punti accumulati e lo storico dei premi nell'app",
      },
      {
        heading: 'Servizi di terze parti',
        body: "L'app utilizza Firebase (Google) per:\n• Autenticazione (Firebase Auth)\n• Notifiche push (Firebase Cloud Messaging)\n• Segnalazione degli arresti anomali (Firebase Crashlytics) — raccoglie modello del dispositivo, versione OS, versione dell'app e tracce di errore per aiutarci a correggere i bug\n\nNon vengono utilizzati SDK pubblicitari. L'app non contiene pubblicità.",
      },
      {
        heading: 'Per quanto tempo conserviamo i tuoi dati',
        body: 'I tuoi dati vengono conservati finché il tuo account è attivo.\n\nSe il tuo account non viene utilizzato per più di 3 anni, potrebbe essere eliminato automaticamente.\n\nNon conserviamo alcun dato dopo la cancellazione dell\'account.',
      },
      {
        heading: 'Come eliminare i tuoi dati',
        body: "Puoi richiedere la cancellazione completa del tuo account e di tutti i dati associati in qualsiasi momento:\n\n1. Nell'app: vai su Account → Elimina il mio account.\n2. Via email: invia una richiesta di cancellazione a " + CONTACT_EMAIL + ".\n\nTutti i dati personali saranno eliminati definitivamente e irreversibilmente entro 30 giorni dalla tua richiesta.",
      },
      {
        heading: 'Condivisione dei dati',
        body: "Non vendiamo, affittiamo né condividiamo i tuoi dati personali con terze parti per scopi commerciali.\n\nI commercianti partner possono vedere il numero di punti e premi associati alla propria carta — non possono vedere i tuoi dati presso altri commercianti.",
      },
      {
        heading: 'Sicurezza',
        body: "Tutti i dati vengono trasmessi tramite connessioni crittografate (HTTPS). L'accesso ai nostri server è limitato e monitorato. Nessun sistema è sicuro al 100%, ma prendiamo molto sul serio la protezione dei dati.",
      },
      {
        heading: 'I tuoi diritti',
        body: "Puoi in qualsiasi momento:\n• Correggere il tuo nome, genere o data di nascita direttamente nell'app\n• Richiedere una copia dei dati che deteniamo su di te\n• Richiedere la cancellazione completa del tuo account e di tutti i dati associati\n\nPer esercitare questi diritti, contattaci a " + CONTACT_EMAIL + '.',
      },
      {
        heading: 'Modifiche a questa informativa',
        body: "Potremmo aggiornare questa informativa sulla privacy di tanto in tanto. In tal caso, ti verrà chiesto di rivedere e accettare le condizioni aggiornate alla prossima apertura dell'app.",
      },
    ],
  },
};

const SUPPORTED_LANGS = Object.keys(content);

const Privacy = () => {
  const { lang, mode } = useParams();
  const resolvedLang = SUPPORTED_LANGS.includes(lang) ? lang : 'en';
  const isApp = mode === 'white';
  const { title, updated, sections } = content[resolvedLang];

  return (
    <>
      {isApp && (
        <style>{`html, body { background: transparent !important; margin: 0; }`}</style>
      )}
      {!isApp && (
        <style>{`
          html, body { margin: 0; padding: 0; }
          a { color: #3b95e8; }
        `}</style>
      )}
      <main style={{
        background: isApp ? 'transparent' : 'linear-gradient(160deg, #0a1628 0%, #0f1f3d 50%, #0a1628 100%)',
        color: '#e8f0fe',
        minHeight: '100vh',
        padding: isApp ? '20px 18px 40px' : '0',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        boxSizing: 'border-box',
      }}>
        {!isApp && (
          <nav style={{
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            padding: '18px 32px',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <a href="/" style={{ fontSize: 13, color: 'rgba(232,240,254,0.45)', textDecoration: 'none' }}>fidelize.org</a>
            <span style={{ fontSize: 12, color: 'rgba(232,240,254,0.25)' }}>/</span>
            <span style={{ fontSize: 13, color: 'rgba(232,240,254,0.65)' }}>{title}</span>
          </nav>
        )}
        <div style={{
          maxWidth: isApp ? '100%' : 720,
          margin: '0 auto',
          padding: isApp ? 0 : '48px 32px 80px',
          boxSizing: 'border-box',
        }}>
          {!isApp && (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 2, color: '#3b95e8', textTransform: 'uppercase', marginBottom: 12 }}>
                Legal
              </div>
              <h1 style={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 800, color: '#fff', margin: '0 0 8px', letterSpacing: -0.5 }}>
                {title}
              </h1>
              <p style={{ fontSize: 13, color: 'rgba(232,240,254,0.35)', margin: '0 0 40px' }}>{updated}</p>
            </>
          )}
          {sections.map((s) => (
            <section key={s.heading} style={{
              marginBottom: isApp ? 22 : 0,
              borderBottom: isApp ? 'none' : '1px solid rgba(255,255,255,0.06)',
              paddingBottom: isApp ? 0 : 28,
              marginTop: isApp ? 0 : 28,
            }}>
              <h2 style={{
                fontSize: isApp ? 11 : 12,
                fontWeight: 700,
                color: '#3b95e8',
                marginBottom: 8,
                marginTop: 0,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}>
                {s.heading}
              </h2>
              <p style={{
                fontSize: isApp ? 13 : 14,
                lineHeight: 1.75,
                color: 'rgba(232,240,254,0.70)',
                whiteSpace: 'pre-line',
                margin: 0,
              }}>
                {s.body}
              </p>
            </section>
          ))}
        </div>
        {!isApp && (
          <footer style={{
            borderTop: '1px solid rgba(255,255,255,0.08)',
            padding: '24px 32px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexWrap: 'wrap', gap: 12,
            maxWidth: 720, margin: '0 auto',
          }}>
            <span style={{ fontSize: 12, color: 'rgba(232,240,254,0.3)' }}>© {new Date().getFullYear()} Alpes Solutions</span>
            <div style={{ display: 'flex', gap: 20 }}>
              <a href="/terms/es" style={{ fontSize: 12, color: 'rgba(232,240,254,0.35)', textDecoration: 'none' }}>Términos de uso</a>
              <a href="/" style={{ fontSize: 12, color: 'rgba(232,240,254,0.35)', textDecoration: 'none' }}>fidelize.org</a>
            </div>
          </footer>
        )}
      </main>
    </>
  );
};

export default Privacy;
